import { useEffect, useMemo, useState, type FormEvent } from "react";
import { DueInput, DueTag } from "./DueDate";
import { isOverdue } from "./format";
import {
  useAddProjectTask,
  useCompleteProject,
  useCompleteProjectTask,
  useCreateProject,
  useCreateReference,
  useHorizons,
  useProjectTasks,
  useProjects,
  useReferences,
  useSpaceParam,
  useTrashReference,
  useUpdateProject,
  useUpdateTask,
} from "./hooks";
import { useProjectHint } from "./projectHint";
import { ReferenceForm, ReferenceRow } from "./ReferencesView";
import type { Horizon, Project, ProjectView as ProjectViewType, Task } from "./types";

/** Wspólne dla wierszy na każdym poziomie drzewa projektów. */
interface TreeProps {
  /** Aktywne i odłożone, także podprojekty; z tego składamy drzewo. */
  all: ProjectViewType[];
  openIds: Set<string>;
  onToggle: (id: string) => void;
  /** Także zarchiwizowane: projekt może wciąż na taki wskazywać. */
  horizons: Horizon[];
}

const HORIZON_STATUSES: Horizon["status"][] = ["active", "archived"];

function childrenOf(all: ProjectViewType[], id: string): ProjectViewType[] {
  return all.filter((view) => view.project.parent_project_id === id);
}

/** Sam projekt i wszyscy pod nim: tych nie da się wybrać jako projektu nadrzędnego. */
function subtreeIds(all: ProjectViewType[], id: string): Set<string> {
  const ids = new Set([id]);
  for (const queue = [id]; queue.length > 0; ) {
    for (const child of childrenOf(all, queue.pop() as string)) {
      if (!ids.has(child.project.id)) {
        ids.add(child.project.id);
        queue.push(child.project.id);
      }
    }
  }
  return ids;
}

export function ProjectsView() {
  const space = useSpaceParam();
  const { data, isLoading, error } = useProjects(space);
  const someday = useProjects(space, "someday");
  const horizons = useHorizons(undefined, HORIZON_STATUSES);
  const create = useCreateProject();
  const requestedId = useProjectHint((s) => s.openProjectId);
  const clearRequest = useProjectHint((s) => s.openProject);

  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [outcome, setOutcome] = useState("");
  const [firstAction, setFirstAction] = useState("");

  const all = useMemo(() => [...(data ?? []), ...(someday.data ?? [])], [data, someday.data]);
  const known = useMemo(() => new Set(all.map((view) => view.project.id)), [all]);
  // Podprojekt mieszka w szufladzie rodzica; gdy rodzica nie widać (inna przestrzeń), jest na wierzchu.
  const isTopLevel = (view: ProjectViewType) =>
    !view.project.parent_project_id || !known.has(view.project.parent_project_id);
  const activeRoots = (data ?? []).filter(isTopLevel);
  const somedayRoots = (someday.data ?? []).filter(isTopLevel);

  function toggle(id: string) {
    setOpenIds((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  // "Otwórz projekt" ze wskazówki po odhaczeniu ostatniego next; podprojekt wymaga otwartych rodziców.
  useEffect(() => {
    if (!requestedId) return;
    setOpenIds((current) => {
      const next = new Set(current);
      let cursor: string | null = requestedId;
      while (cursor && !next.has(cursor)) {
        next.add(cursor);
        cursor = all.find((view) => view.project.id === cursor)?.project.parent_project_id ?? null;
      }
      return next;
    });
    clearRequest(null);
  }, [requestedId, clearRequest, all]);

  function onCreate(event: FormEvent) {
    event.preventDefault();
    if (!title.trim() || !firstAction.trim()) return;
    create.mutate(
      {
        title: title.trim(),
        outcome: outcome.trim() || null,
        first_action: firstAction.trim(),
        space: space ?? null,
      },
      {
        onSuccess: () => {
          setTitle("");
          setOutcome("");
          setFirstAction("");
          setShowForm(false);
        },
      },
    );
  }

  const tree: TreeProps = { all, openIds, onToggle: toggle, horizons: horizons.data ?? [] };

  return (
    <div className="view">
      <button className="btn btn--primary btn--block" onClick={() => setShowForm(!showForm)}>
        {showForm ? "Anuluj" : "+ Nowy projekt"}
      </button>

      {showForm && (
        <form className="clarify" onSubmit={onCreate}>
          <label className="field">
            <span className="field__label">Nazwa projektu</span>
            <input className="field__input" value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Rezultat</span>
            <input
              className="field__input"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field__label">Pierwsze next action *</span>
            <input
              className="field__input"
              value={firstAction}
              onChange={(e) => setFirstAction(e.target.value)}
              required
            />
          </label>
          <div className="clarify__buttons">
            <button
              className="btn btn--primary"
              type="submit"
              disabled={!title.trim() || !firstAction.trim() || create.isPending}
            >
              Utwórz
            </button>
          </div>
          {create.error && <p className="error">{(create.error as Error).message}</p>}
        </form>
      )}

      {isLoading && <p className="muted">Ładowanie…</p>}
      {error && <p className="error">Nie udało się pobrać projektów.</p>}
      {data && data.length === 0 && <p className="muted">Brak aktywnych projektów.</p>}

      <ul className="rows">
        {activeRoots.map((view) => (
          <ProjectRow key={view.project.id} view={view} {...tree} />
        ))}
      </ul>

      {somedayRoots.length > 0 && (
        <section className="group">
          <h3 className="group__title">
            Kiedyś <span className="group__count">{somedayRoots.length}</span>
          </h3>
          <ul className="rows">
            {somedayRoots.map((view) => (
              <ProjectRow key={view.project.id} view={view} {...tree} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ProjectRow({
  view,
  all,
  openIds,
  onToggle,
  horizons,
}: { view: ProjectViewType } & TreeProps) {
  const { project, open_next_count, stalled } = view;
  const horizon = horizons.find((item) => item.id === project.horizon_id);
  const open = openIds.has(project.id);
  const subprojects = childrenOf(all, project.id);
  const tasks = useProjectTasks(open ? project.id : null);
  const addTask = useAddProjectTask();
  const completeTask = useCompleteProjectTask(project.id);
  const updateTask = useUpdateTask();
  const completeProject = useCompleteProject();
  const updateProject = useUpdateProject();
  const [newTask, setNewTask] = useState("");
  const [newTaskDue, setNewTaskDue] = useState("");
  const [newTaskLater, setNewTaskLater] = useState(false);
  const [editing, setEditing] = useState(false);

  const items = tasks.data?.items ?? [];
  const current = items.filter((task) => task.status !== "someday");
  const futureSteps = items.filter((task) => task.status === "someday");

  function onAdd(event: FormEvent) {
    event.preventDefault();
    const title = newTask.trim();
    if (!title) return;
    addTask.mutate(
      {
        projectId: project.id,
        input: { title, status: newTaskLater ? "someday" : "next", due_date: newTaskDue || null },
      },
      {
        onSuccess: () => {
          setNewTask("");
          setNewTaskDue("");
        },
      },
    );
  }

  return (
    <li className="row row--block">
      <div className="row__head" onClick={() => onToggle(project.id)} role="button" tabIndex={0}>
        <div className="row__main">
          <span className="row__title">
            {project.title}
            {stalled && <span className="badge badge--warn">utknął</span>}
          </span>
          <span className="row__meta">
            {project.outcome && <span className="row__note">{project.outcome}</span>}
            <span>{open_next_count} next</span>
            {horizon && <span>{horizon.title}</span>}
            {view.open_subproject_count > 0 && (
              <span>podprojekty: {view.open_subproject_count}</span>
            )}
          </span>
        </div>
        <span className="row__chevron">{open ? "▾" : "▸"}</span>
      </div>

      {open && (
        <div className="drawer">
          {stalled && (
            <p className="hint" role="status">
              Ten projekt nie ma następnego kroku. Dopisz go poniżej albo zakończ projekt.
            </p>
          )}

          {editing ? (
            <PlanForm
              project={project}
              all={all}
              busy={updateProject.isPending}
              onCancel={() => setEditing(false)}
              onSubmit={(input) =>
                updateProject.mutate(
                  { id: project.id, input },
                  { onSuccess: () => setEditing(false) },
                )
              }
            />
          ) : (
            <PlanSummary project={project} />
          )}
          {updateProject.error && <p className="error">{(updateProject.error as Error).message}</p>}

          <HorizonPicker
            project={project}
            horizons={horizons}
            busy={updateProject.isPending}
            onChange={(horizonId) =>
              updateProject.mutate({ id: project.id, input: { horizon_id: horizonId } })
            }
          />

          {tasks.isLoading && <p className="muted">Ładowanie…</p>}
          <ul className="rows rows--nested">
            {current.map((task) => (
              <TaskRow key={task.id} task={task} onDone={() => completeTask.mutate(task.id)} />
            ))}
          </ul>

          {futureSteps.length > 0 && (
            <section className="project-section">
              <h4 className="project-section__title">Przyszłe kroki</h4>
              <ul className="rows rows--nested">
                {futureSteps.map((task) => (
                  <li key={task.id} className={`row ${isOverdue(task) ? "row--overdue" : ""}`}>
                    <div className="row__main">
                      <span className="row__title">{task.title}</span>
                      <span className="row__meta">
                        <DueTag task={task} />
                      </span>
                    </div>
                    <div className="row__actions">
                      <button
                        className="btn btn--small"
                        title="Krok jest już wykonalny"
                        onClick={() =>
                          updateTask.mutate({ id: task.id, input: { status: "next" } })
                        }
                      >
                        → next
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <form className="capture capture--wrap" onSubmit={onAdd}>
            <input
              className="capture__input"
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              placeholder={newTaskLater ? "Przyszły krok projektu…" : "Dodaj zadanie do projektu…"}
              aria-label="Dodaj zadanie do projektu"
            />
            <select
              className="capture__select"
              value={newTaskLater ? "later" : "now"}
              onChange={(e) => setNewTaskLater(e.target.value === "later")}
              aria-label="Kiedy"
            >
              <option value="now">next</option>
              <option value="later">później</option>
            </select>
            <DueInput value={newTaskDue} onChange={setNewTaskDue} />
            <button className="btn btn--primary" type="submit" disabled={!newTask.trim()}>
              Dodaj
            </button>
          </form>

          <section className="project-section">
            <h4 className="project-section__title">Podprojekty</h4>
            {subprojects.length > 0 && (
              <ul className="rows rows--nested">
                {subprojects.map((child) => (
                  <ProjectRow
                    key={child.project.id}
                    view={child}
                    all={all}
                    openIds={openIds}
                    onToggle={onToggle}
                    horizons={horizons}
                  />
                ))}
              </ul>
            )}
            <SubprojectForm parent={project} />
          </section>

          <ProjectReferences project={project} />

          <div className="project-actions">
            {!editing && (
              <button
                className="btn"
                onClick={() => {
                  updateProject.reset();
                  setEditing(true);
                }}
              >
                Edytuj plan
              </button>
            )}
            <button
              className="btn"
              disabled={updateProject.isPending}
              onClick={() =>
                updateProject.mutate({
                  id: project.id,
                  input: { status: project.status === "someday" ? "active" : "someday" },
                })
              }
            >
              {project.status === "someday" ? "Wznów projekt" : "Odłóż na kiedyś"}
            </button>
            <button
              className="btn"
              onClick={() => {
                if (confirm("Zakończyć projekt? Otwarte zadania trafią do kosza.")) {
                  completeProject.mutate(project.id);
                }
              }}
            >
              Zakończ projekt
            </button>
          </div>
          {completeProject.error && (
            <p className="error">{(completeProject.error as Error).message}</p>
          )}
        </div>
      )}
    </li>
  );
}

function TaskRow({ task, onDone }: { task: Task; onDone: () => void }) {
  return (
    <li className={`row ${isOverdue(task) ? "row--overdue" : ""}`}>
      <label className="row__check">
        <input
          type="checkbox"
          checked={task.status === "done"}
          disabled={task.status === "done" || task.status === "trashed"}
          onChange={onDone}
          aria-label={`Zrobione: ${task.title}`}
        />
      </label>
      <div className="row__main">
        <span className={`row__title ${task.status === "done" ? "row__title--done" : ""}`}>
          {task.title}
        </span>
        <span className="row__meta">
          <span>{task.status}</span>
          {task.context && <span>{task.context}</span>}
          <DueTag task={task} />
        </span>
      </div>
    </li>
  );
}

/** Bez obszarów i celów nie ma czego wybierać, więc pola też nie ma: nic nie wciskamy. */
function HorizonPicker({
  project,
  horizons,
  busy,
  onChange,
}: {
  project: Project;
  horizons: Horizon[];
  busy: boolean;
  onChange: (horizonId: string | null) => void;
}) {
  const available = horizons.filter(
    (item) =>
      item.status === "active" &&
      (!project.space_id || !item.space_id || item.space_id === project.space_id),
  );
  const areas = available.filter((item) => item.level === 2);
  const goals = available.filter((item) => item.level === 3);
  const current = horizons.find((item) => item.id === project.horizon_id);
  const currentHidden = current && !areas.includes(current) && !goals.includes(current);
  if (areas.length + goals.length === 0 && !current) return null;

  return (
    <label className="field">
      <span className="field__label">Służy (obszar lub cel)</span>
      <select
        className="field__input"
        value={project.horizon_id ?? ""}
        disabled={busy}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">— brak —</option>
        {currentHidden && (
          <option value={current.id} disabled={current.status !== "active"}>
            {current.title}
            {current.status === "active" ? "" : " (w archiwum)"}
          </option>
        )}
        {areas.length > 0 && (
          <optgroup label="Obszary">
            {areas.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </optgroup>
        )}
        {goals.length > 0 && (
          <optgroup label="Cele">
            {goals.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </optgroup>
        )}
      </select>
    </label>
  );
}

function PlanSummary({ project }: { project: Project }) {
  const parts: [string, string | null][] = [
    ["Po co", project.purpose],
    ["Zasady", project.principles],
    ["Rezultat", project.outcome],
    ["Burza mózgów", project.brainstorm],
  ];
  const filled = parts.filter(([, value]) => value);
  if (filled.length === 0) return null;
  return (
    <dl className="plan">
      {filled.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function PlanForm({
  project,
  all,
  busy,
  onSubmit,
  onCancel,
}: {
  project: Project;
  all: ProjectViewType[];
  busy: boolean;
  onSubmit: (input: {
    title: string;
    outcome: string | null;
    purpose: string | null;
    principles: string | null;
    brainstorm: string | null;
    parent_project_id: string | null;
  }) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(project.title);
  const [outcome, setOutcome] = useState(project.outcome ?? "");
  const [purpose, setPurpose] = useState(project.purpose ?? "");
  const [principles, setPrinciples] = useState(project.principles ?? "");
  const [brainstorm, setBrainstorm] = useState(project.brainstorm ?? "");
  const [parentId, setParentId] = useState(project.parent_project_id ?? "");

  const excluded = subtreeIds(all, project.id);
  // Aktywny projekt nie może mieć odłożonego rodzica; serwer i tak by to odrzucił.
  const parents = all.filter(
    (view) =>
      !excluded.has(view.project.id) &&
      (view.project.status === "active" || project.status === "someday"),
  );
  const parentHidden =
    project.parent_project_id && !parents.some((v) => v.project.id === project.parent_project_id);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      outcome: outcome.trim() || null,
      purpose: purpose.trim() || null,
      principles: principles.trim() || null,
      brainstorm: brainstorm.trim() || null,
      parent_project_id: parentId || null,
    });
  }

  return (
    <form className="project-edit" onSubmit={handleSubmit}>
      <label className="field">
        <span className="field__label">Nazwa projektu</span>
        <input
          className="field__input"
          value={title}
          maxLength={512}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
      </label>
      <label className="field">
        <span className="field__label">Po co? (cel)</span>
        <textarea
          className="field__input"
          rows={2}
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field__label">Zasady i standardy</span>
        <textarea
          className="field__input"
          rows={2}
          value={principles}
          onChange={(e) => setPrinciples(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field__label">Rezultat (jak wygląda ukończone?)</span>
        <input
          className="field__input"
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field__label">Burza mózgów</span>
        <textarea
          className="field__input"
          rows={4}
          value={brainstorm}
          onChange={(e) => setBrainstorm(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field__label">Projekt nadrzędny</span>
        <select
          className="field__input"
          value={parentId}
          onChange={(e) => setParentId(e.target.value)}
        >
          <option value="">— brak —</option>
          {parentHidden && (
            <option value={project.parent_project_id as string}>(obecny, poza tym widokiem)</option>
          )}
          {parents.map((view) => (
            <option key={view.project.id} value={view.project.id}>
              {view.project.title}
            </option>
          ))}
        </select>
      </label>
      <div className="clarify__buttons">
        <button className="btn btn--primary" type="submit" disabled={!title.trim() || busy}>
          Zapisz
        </button>
        <button className="btn" type="button" onClick={onCancel}>
          Anuluj
        </button>
      </div>
    </form>
  );
}

/** Podprojekt to pełny projekt: też potrzebuje pierwszego kroku (inwariant GTD). */
function SubprojectForm({ parent }: { parent: Project }) {
  const create = useCreateProject();
  const [title, setTitle] = useState("");
  const [firstAction, setFirstAction] = useState("");

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim() || !firstAction.trim()) return;
    create.mutate(
      {
        title: title.trim(),
        first_action: firstAction.trim(),
        parent_project_id: parent.id,
        space: parent.space_id,
      },
      {
        onSuccess: () => {
          setTitle("");
          setFirstAction("");
        },
      },
    );
  }

  return (
    <form className="project-edit" onSubmit={onSubmit}>
      <div className="field-row">
        <input
          className="field__input"
          value={title}
          maxLength={512}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Nowy podprojekt…"
          aria-label="Nazwa podprojektu"
        />
        <input
          className="field__input"
          value={firstAction}
          maxLength={512}
          onChange={(e) => setFirstAction(e.target.value)}
          placeholder="Pierwszy krok…"
          aria-label="Pierwszy krok podprojektu"
        />
        <button
          className="btn btn--primary"
          type="submit"
          disabled={!title.trim() || !firstAction.trim() || create.isPending}
        >
          Dodaj
        </button>
      </div>
      {create.error && <p className="error">{(create.error as Error).message}</p>}
    </form>
  );
}

function ProjectReferences({ project }: { project: Project }) {
  const { data } = useReferences({ project_id: project.id, limit: 200 });
  const create = useCreateReference();
  const trash = useTrashReference();
  const [adding, setAdding] = useState(false);

  return (
    <section className="project-section">
      <h4 className="project-section__title">Materiały</h4>
      {data && data.items.length > 0 && (
        <ul className="rows rows--nested">
          {data.items.map((item) => (
            <ReferenceRow
              key={item.id}
              item={item}
              onTrash={() => {
                if (confirm(`Wyrzucić materiał „${item.title}”?`)) trash.mutate(item.id);
              }}
            />
          ))}
        </ul>
      )}
      {adding ? (
        <ReferenceForm
          busy={create.isPending}
          error={create.error}
          submitLabel="Zapisz materiał"
          onCancel={() => setAdding(false)}
          onSubmit={(input) =>
            create.mutate(
              { ...input, project_id: project.id },
              { onSuccess: () => setAdding(false) },
            )
          }
        />
      ) : (
        <button className="btn" onClick={() => setAdding(true)}>
          + Dodaj materiał
        </button>
      )}
    </section>
  );
}
